import React, { useState } from 'react'
import { authStaff, dateToHumanFormat } from '../../utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { selectAccessToken, selectStaff } from '../../slices/staffSlice';
import { fetchRequests } from '../../Requests/getRequests';
import { show } from '../../slices/toastSlice';
import { useNavigate } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowDown01Icon, CheckmarkCircle01Icon, Cancel01Icon, UserIcon, CreditCardIcon, Call02Icon, Calendar03Icon } from '@hugeicons/core-free-icons';

function DiffCell({ oldVal, newVal }) {
    const changed = String(oldVal) !== String(newVal);
    return (
        <>
            <td className="px-5 py-3.5 text-sm font-mono text-gray-600 dark:text-gray-400">{oldVal ?? '—'}</td>
            <td className={`px-5 py-3.5 text-sm font-mono font-semibold ${changed ? 'text-amber-700 dark:text-amber-400' : 'text-gray-600 dark:text-gray-400'}`}>
                {newVal ?? '—'}
                {changed && <span className="ml-1.5 text-[10px] font-sans font-medium text-amber-500 bg-amber-50 border border-amber-100 rounded px-1 py-0.5">changed</span>}
            </td>
        </>
    );
}

// derive a request's real status from the document (status flag + approvals),
// so it renders correctly whether browsing a tab or viewing mixed search results
const deriveStatus = (item) => {
    if (item?.status) return 'approved';
    if ((item?.approvals || []).some((app) => app?.status === false)) return 'rejected';
    return 'pending';
};

const STATUS_PILL = {
    pending:  { label: 'Pending',  className: 'text-amber-600 bg-amber-50 border-amber-100 dark:bg-amber-500/10 dark:border-amber-500/20' },
    approved: { label: 'Approved', className: 'text-emerald-600 bg-emerald-50 border-emerald-100 dark:bg-emerald-500/10 dark:border-emerald-500/20' },
    rejected: { label: 'Rejected', className: 'text-red-600 bg-red-50 border-red-100 dark:bg-red-500/10 dark:border-red-500/20' },
};

function Item({ item, onActionDone }) {

    const accessToken = useSelector(selectAccessToken);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const staff = useSelector(selectStaff);
    const status = deriveStatus(item);
    const disabledAgent = item?.approvals.filter((app) => app.email === staff?.email);
    const [toggled, setToggled] = useState(false);
    const [process, setProcess] = useState(false);
    const [denyProcess, setDenyProcess] = useState(false);

    const handleRequest = async (status, setLoading) => {
        setLoading(true);
        const res = await fetchRequests(`/loans/staff/reversals`, "PATCH", authStaff(accessToken), {}, { _id: item?._id, status });
        setLoading(false);

        if (res?.code === 401) {
            sessionStorage.clear();
            navigate("/login");
            dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login to continue." }));
            return;
        }

        if (res?.err) {
            dispatch(show({ type: "error", state: true, message: res?.err }));
            return;
        }

        dispatch(show({ type: "success", state: true, message: res?.message }));
        setTimeout(() => onActionDone?.(), 2700);
    };

    const handleApprove = () => handleRequest(true, setProcess);
    const handleDeny = () => handleRequest(false, setDenyProcess);

    return (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
            {/* Header row */}
            <button
                type="button"
                onClick={() => setToggled(t => !t)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/60 dark:hover:bg-gray-700/40 transition-colors text-left"
            >
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800 dark:text-gray-100">
                        <HugeiconsIcon icon={UserIcon} size={13} className="text-gray-400 shrink-0" />
                        {item?.client}
                    </span>
                    <span className="hidden sm:flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                        <HugeiconsIcon icon={CreditCardIcon} size={13} className="text-gray-400 shrink-0" />
                        {item?.loanType}
                    </span>
                    <span className="hidden sm:flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                        <HugeiconsIcon icon={Call02Icon} size={13} className="text-gray-400 shrink-0" />
                        {item?.phone}
                    </span>
                    <span className="hidden md:flex items-center gap-1.5 text-sm text-gray-400 dark:text-gray-500">
                        <HugeiconsIcon icon={Calendar03Icon} size={13} className="text-gray-400 dark:text-gray-500 shrink-0" />
                        {dateToHumanFormat(item?.createdAtms)}
                    </span>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${STATUS_PILL[status].className}`}>
                        {STATUS_PILL[status].label}
                    </span>
                    <div className={`w-7 h-7 rounded-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 flex items-center justify-center transition-transform duration-200 ${toggled ? 'rotate-180' : ''}`}>
                        <HugeiconsIcon icon={ArrowDown01Icon} size={14} className="text-gray-500 dark:text-gray-400" />
                    </div>
                </div>
            </button>

            {/* Expanded content */}
            {toggled && (
                <div className="border-t border-gray-100 dark:border-gray-700">
                    {/* Diff table */}
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead>
                                <tr className="bg-gray-50/60 dark:bg-gray-900/60">
                                    <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide w-1/3">Detail</th>
                                    <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide w-1/3">Old</th>
                                    <th className="px-5 py-2.5 text-left text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide w-1/3">New</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                                <tr className="hover:bg-gray-50/40 dark:hover:bg-gray-700/30 transition-colors">
                                    <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-gray-400">Transaction ID</td>
                                    <DiffCell oldVal={item?.oldTransID} newVal={item?.newTransID} />
                                </tr>
                                <tr className="hover:bg-gray-50/40 dark:hover:bg-gray-700/30 transition-colors">
                                    <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-gray-400">Transaction Date</td>
                                    <DiffCell oldVal={dateToHumanFormat(item?.oldDate)} newVal={dateToHumanFormat(item?.newDate)} />
                                </tr>
                                <tr className="hover:bg-gray-50/40 dark:hover:bg-gray-700/30 transition-colors">
                                    <td className="px-5 py-3.5 text-sm text-gray-500 dark:text-gray-400">Amount</td>
                                    <DiffCell oldVal={item?.oldAmount} newVal={item?.newAmount} />
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {/* Comment */}
                    {item?.comment && (
                        <div className="mx-5 my-4 px-4 py-3 bg-amber-50/60 dark:bg-amber-500/10 border border-amber-100 dark:border-amber-500/20 rounded-lg">
                            <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wide mb-1">Reason</p>
                            <p className="text-sm text-gray-700 dark:text-gray-300">{item?.comment}</p>
                        </div>
                    )}

                    {/* Actions */}
                    {status === 'pending' && disabledAgent.length === 0 && (
                        <div className="flex items-center gap-3 px-5 py-4 border-t border-gray-100 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-900/40">
                            <button
                                type="button"
                                onClick={handleApprove}
                                disabled={process || denyProcess}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg hover:bg-emerald-100 transition-colors disabled:opacity-50"
                            >
                                {process ? (
                                    <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                    </svg>
                                ) : (
                                    <HugeiconsIcon icon={CheckmarkCircle01Icon} size={15} />
                                )}
                                Approve
                            </button>

                            <button
                                type="button"
                                onClick={handleDeny}
                                disabled={process || denyProcess}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-100 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                            >
                                {denyProcess ? (
                                    <svg className="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                                    </svg>
                                ) : (
                                    <HugeiconsIcon icon={Cancel01Icon} size={15} />
                                )}
                                Deny
                            </button>
                        </div>
                    )}

                    {status === 'pending' && disabledAgent.length > 0 && (
                        <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-900/40">
                            <p className="text-xs text-gray-400 flex items-center gap-1.5">
                                <HugeiconsIcon icon={CheckmarkCircle01Icon} size={13} className="text-emerald-500" />
                                You have already responded to this request
                            </p>
                        </div>
                    )}

                    {status !== 'pending' && (
                        <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-900/40">
                            <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${status === 'approved' ? 'text-emerald-600' : 'text-red-600'}`}>
                                <HugeiconsIcon icon={status === 'approved' ? CheckmarkCircle01Icon : Cancel01Icon} size={13} />
                                {status === 'approved' ? 'Approved' : 'Rejected'}
                            </span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default Item
